/*
  DataContext.js

  Purpose:
  - Provide a small, app-wide data store for locations and measurements fetched
    from the backend APIs. Components can consume `DataContext` to access
    `locations`, `measurements`, and `loading` without prop-drilling.

  What it exposes (context value):
  - locations: Array of location objects (example shape: { id, name, latitude, longitude, region, ... })
  - measurements: Array of measurement objects (example shape: { id, location_id, date, toxin_value, sst, ... })
  - loading: boolean indicating if the initial data load is in progress

  Environment variables used:
  - REACT_APP_LOCATIONS_API -> URL to fetch locations (should return JSON with `locations` array)
  - REACT_APP_MEASUREMENTS_API -> URL to fetch measurements (should return JSON with `measurements` array)

  Example usage:
    const { locations, measurements, loading } = useContext(DataContext);

*/

import React, { createContext, useState, useEffect } from "react";

export const DataContext = createContext();

export const DataProvider = ({ children }) => {
  const [locations, setLocations] = useState([]);
  const [measurements, setMeasurements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // loadData: simple one-time fetch for locations and measurements.
    const loadData = async () => {
      try {
        // NOTE: env vars must be defined in your .env or hosting provider.
        // If undefined, fetch will throw — consider validating before calling.
        const locRes = await fetch(process.env.REACT_APP_LOCATIONS_API);
        const locData = await locRes.json();
        // Backend is expected to return an object like { locations: [...] }
        if (locData.locations) setLocations(locData.locations);

        const measRes = await fetch(process.env.REACT_APP_MEASUREMENTS_API);
        const measData = await measRes.json();
        // Backend expected to return { measurements: [...] }
        if (measData.measurements) setMeasurements(measData.measurements);
      } catch (err) {
        console.error("Error loading data:", err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  return (
    <DataContext.Provider value={{ locations, measurements, loading }}>
      {children}
    </DataContext.Provider>
  );
};